// filepath: src/components/Auth/Login.tsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAuth, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import app from '../../services/firebase'; // Import the initialized Firebase app
import ErrorBubble from '../ErrorBubble/ErrorBubble';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const auth = getAuth(app); // Get the Firebase Auth instance

    if (!email || !password) {
      setErrorMessage('Email and password are required.');
      setTimeout(() => setErrorMessage(null), 3000); // Clear the error after 3 seconds
      return;
    }

    try {
      // Sign in the user with email and password
      await signInWithEmailAndPassword(auth, email, password);
      // Redirect to the main page upon successful login
      navigate('/main');
    } catch (err: any) {
      console.log('Firebase Error Code:', err.code); // Log the error code
      // Map Firebase error codes to user-friendly messages
      const errorMessage = handleLoginError(err.code);
      setErrorMessage(errorMessage); // Display the user-friendly error message
      setTimeout(() => setErrorMessage(null), 3000); // Clear the error after 3 seconds
    }
  };

  // Map Firebase error codes to user-friendly messages
  const handleLoginError = (errorCode: string): string => {
    switch (errorCode) {
      case 'auth/user-not-found':
        return 'No account found with this email. Please check your email or sign up.';
      case 'auth/invalid-email':
        return 'The email address is not valid. Please check and try again.';
      case 'auth/wrong-password':
        return 'The password is incorrect. Please try again.';
      case 'auth/too-many-requests':
        return 'Too many failed login attempts. Please try again later.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your internet connection and try again.';
      case 'auth/email-already-in-use':
        return 'This email is already in use. Please use a different email or log in.';
      case 'auth/weak-password':
        return 'The password is too weak. Please use a stronger password.';
      case 'auth/internal-error':
        return 'An internal error occurred. Please try again later.';
      case 'auth/operation-not-allowed':
        return 'This operation is not allowed. Please contact support.';
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setErrorMessage('Please enter your email to reset your password.');
      setTimeout(() => setErrorMessage(null), 3000); // Clear the error after 3 seconds
      return;
    }

    try {
      const auth = getAuth(app); // Get the Firebase Auth instance
      await sendPasswordResetEmail(auth, email);
      setErrorMessage('Password reset email sent. Please check your inbox.');
      setTimeout(() => setErrorMessage(null), 5000); // Clear the message after 5 seconds
    } catch (err: any) {
      console.error('Error sending password reset email:', err);
      const errorMessage = handleLoginError(err.code); // Reuse the error handler
      setErrorMessage(errorMessage);
      setTimeout(() => setErrorMessage(null), 3000); // Clear the error after 3 seconds
    }
  };

  return (
    <div className="page-container">
      <h1>Login</h1>
      {/* Error Bubble */}
      <ErrorBubble message={errorMessage} />
      <form className="auth-form" onSubmit={handleLogin}>
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <button type="submit">Login</button>
      </form>
      <p>
        Don't have an account? <Link to="/register">Register here</Link>
      </p>
      <p>
        <button
          type="button"
          className="forgot-password-link"
          onClick={() => handleForgotPassword()}
        >
          Forgot Password?
        </button>
      </p>
    </div>
  );
};

export default Login;